import React, { Suspense, useRef, useState, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Stars, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import RunnerCharacter from './RunnerCharacter';
import ShatterWall from './ShatterWall';
import audioManager from '../../utils/audioManager';

const PathContent = ({ roadOffset }) => {
    const texture = useTexture(process.env.PUBLIC_URL + '/assets/gameplay/path.png');

    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1, 100);

    useFrame(() => {
        if (texture) {
            texture.offset.y = roadOffset * 0.2;
        }
    });

    return (
        <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, -2.85, -200]}
        >
            <planeGeometry args={[10, 500]} />
            <meshStandardMaterial map={texture} />
        </mesh>
    );
};

const Path = (props) => (
    <Suspense fallback={null}>
        <PathContent {...props} />
    </Suspense>
);

const SceneContent = ({ wordList, currentWordIndex, result, onReachPlayer, onShatterComplete, isGameActive, speed = 5 }) => {
    const totalDistanceRef = useRef(0);
    const hitTimeRef = useRef(0);
    const isCollidingRef = useRef(false);
    const [totalDistance, setTotalDistance] = useState(0);

    // We space walls by 25 units
    // Wall 0 at Z = -20. To reach 1.2, it moves 21.2 units.
    const milestoneSpacing = 25;
    const initialWallZ = -20;
    const stopAtZ = 1.2;
    const milestone0 = stopAtZ - initialWallZ; // 21.2

    useFrame((state, delta) => {
        if (!isGameActive) return;

        const time = state.clock.getElapsedTime();
        const currentWord = wordList[currentWordIndex];

        // The current block distance
        const currentMilestone = milestone0 + (currentWordIndex * milestoneSpacing);

        // Should we be blocked?
        // If the current word is NOT cleared and NOT skipped, we block at its milestone.
        const isWordFinished = currentWord.status === 'cleared' || currentWord.status === 'skipped';

        if (!isWordFinished) {
            // Check if we reached the milestone
            if (totalDistanceRef.current < currentMilestone) {
                totalDistanceRef.current += speed * delta;
                setTotalDistance(totalDistanceRef.current);
                isCollidingRef.current = false;
            } else {
                // We are at the milestone and blocked
                if (!isCollidingRef.current) {
                    isCollidingRef.current = true;
                    hitTimeRef.current = time;
                    audioManager.playSFX('sfxWallBounce');
                    onReachPlayer();
                }

                // Bounce math
                const hitElapsed = time - hitTimeRef.current;
                const period = 1.5;
                const t = (hitElapsed % period) / period;
                const bounceAmount = 4 * t * (1 - t) * 1.5;

                // We modulate the totalDistance visually for the bounce
                setTotalDistance(currentMilestone - bounceAmount);
            }
        } else {
            // Word is finished! Move forward freely
            totalDistanceRef.current += speed * delta;
            setTotalDistance(totalDistanceRef.current);
            isCollidingRef.current = false;
        }
    });

    // Render upcoming walls
    const visibleWalls = useMemo(() => {
        // Render current word and a few ahead
        return wordList.slice(currentWordIndex, currentWordIndex + 3);
    }, [wordList, currentWordIndex]);

    return (
        <>
            <ambientLight intensity={0.8} />
            <directionalLight position={[10, 10, 5]} intensity={1} />

            <Path roadOffset={totalDistance} />

            {visibleWalls.map((word, idx) => {
                const globalIndex = currentWordIndex + idx;
                const wallInitZ = initialWallZ - (globalIndex * milestoneSpacing);
                const wallCurrentZ = wallInitZ + totalDistance;

                if (wallCurrentZ > 10) return null;

                return (
                    <Suspense key={word.instanceId} fallback={null}>
                        <ShatterWall
                            word={word.word}
                            image={word.image}
                            status={word.status}
                            currentZ={wallCurrentZ}
                            onShatterComplete={onShatterComplete}
                            isGameActive={isGameActive}
                        />
                    </Suspense>
                );
            })}

            <RunnerCharacter isBouncing={result === 'incorrect'} />

            <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
            <fog attach="fog" args={['#1a1a2e', 10, 60]} />
        </>
    );
};

const RunnerScene = ({ wordList, currentWordIndex, result, onReachPlayer, onShatterComplete, isGameActive, speed }) => {
    return (
        <div className="runner-scene-container">
            <Canvas
                camera={{ position: [0, 2, 8], fov: 45 }}
                gl={{ alpha: true, antialias: true }}
                onCreated={({ gl, camera }) => {
                    gl.setClearColor(0x000000, 0);
                    camera.lookAt(0, -0.5, 0);
                }}
            >
                <SceneContent
                    wordList={wordList}
                    currentWordIndex={currentWordIndex}
                    result={result}
                    onReachPlayer={onReachPlayer}
                    onShatterComplete={onShatterComplete}
                    isGameActive={isGameActive}
                    speed={speed}
                />
            </Canvas>
        </div>
    );
};

export default RunnerScene;
